import { BaseApiErrorResponse } from "@/services/api-config"
import "client-only"

import { useSearchParams } from "next/navigation"
import { useCallback } from "react"

type RecaptchaAction = "LOGIN" | "PASSWORD_RESET"
export type RecaptchaPlatform = "web" | "android" | "ios"

export function useRecaptcha(action: RecaptchaAction) {
  const searchParams = useSearchParams()
  const recaptchaType =
    searchParams.get("native_r") === "true" ? "native" : "web"

  return useCallback(
    async () => await getRecaptchaToken(recaptchaType, action),
    [action, recaptchaType],
  )
}

async function getRecaptchaToken(
  type: "web" | "native",
  action: "LOGIN" | "PASSWORD_RESET",
): Promise<{ token: string; platform: RecaptchaPlatform }> {
  const getWebToken = async () => {
    try {
      return await window.grecaptcha.enterprise.execute(
        window.APP_KEY_RE_CAPTCHA,
        { action },
      )
    } catch {
      console.error("Could not get recaptcha token")
      return ""
    }
  }

  if (type === "web") {
    const token = await getWebToken()
    return { token, platform: "web" }
  }

  window.postMessage({ operation: "recaptcha_token_request", action }, "*")

  return new Promise((resolve) => {
    const messageHandler = (
      event: MessageEvent<{
        operation: "recaptcha_token_response"
        token: string
        platform: "android" | "ios"
      }>,
    ) => {
      if (event.data.operation === "recaptcha_token_response") {
        window.removeEventListener("message", messageHandler)
        resolve({ token: event.data.token, platform: event.data.platform })
      }
    }

    window.addEventListener("message", messageHandler)
  })
}

export function isRecaptchaError(errorData: BaseApiErrorResponse) {
  const errorObj = errorData.error
  const errorInfos = errorObj?.errorInfos

  if (!errorInfos) return false

  return (
    errorObj.type === "VALIDATION" &&
    errorObj.message?.includes("qr failure") &&
    Array.isArray(errorInfos.void) &&
    errorInfos.void.some((err) => err === "err.void")
  )
}
